import type { AiModel } from '@/types/AiModel.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import type { ReasoningEffort } from '@/types/ReasoningEffort.ts';

export const getDefaultReasoningEffort = (
    model: AiModel | undefined,
): ReasoningEffort | undefined => {
    const supported = model?.supportedReasoningEfforts ?? [];
    if (supported.includes('low')) return 'low';
    if (model?.defaultReasoningEffort && supported.includes(model.defaultReasoningEffort)) {
        return model.defaultReasoningEffort;
    }
    if (supported.includes('none')) return 'none';

    return supported[0];
};

export const normalizeReasoningEffort = (
    model: AiModel | undefined,
    effort: string | undefined,
): ReasoningEffort | undefined => {
    const supportedEffort = model?.supportedReasoningEfforts.find(
        (candidate) => candidate === effort,
    );
    if (supportedEffort) return supportedEffort;

    return getDefaultReasoningEffort(model);
};

export const resolveClientModelId = (
    prompt: PromptConfig,
    aiModels: Record<AiModel['id'], AiModel>,
    defaultModelId: string,
    replacements: Record<string, string>,
): string | undefined => {
    const isCompatible = (modelId: string) => {
        const model = aiModels[modelId];
        return (
            model !== undefined &&
            (!prompt.includeScreenCapture || model.supportsImage) &&
            (prompt.formatAs !== 'choice' || model.supportsStructuredOutput)
        );
    };

    if (isCompatible(prompt.modelId)) return prompt.modelId;

    let replacement = prompt.modelId;
    const visited = new Set<string>();
    while (replacements[replacement] && !visited.has(replacement)) {
        visited.add(replacement);
        replacement = replacements[replacement];
    }
    if (replacement !== prompt.modelId && isCompatible(replacement)) return replacement;
    if (isCompatible(defaultModelId)) return defaultModelId;

    return Object.keys(aiModels).find(isCompatible);
};
